<template>
  <app-client-layout
    v-bind="flashObject"
    title="体験模試">
    <template #header>
      <h2 class="text-xl font-semibold leading-tight text-gray-800">
        模試の受験/購入
      </h2>
    </template>
    <p
      v-if="$page.props.flash.message"
      class="text-red-400 text-lg font-black mt-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      {{ $page.props.flash.message }}
    </p>

    <div class="mx-auto">
      <div class="border-2 border-double border-red-500 bg-white p-4 rounded-lg max-w-screen-md mx-auto my-6">
        <div class="flex items-center justify-center">
          <svg
            id="_x32_"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            x="0px"
            y="0px"
            viewBox="0 0 512 512"
            style="width: 40px; height: 40px; opacity: 1"
            xml:space="preserve">
            <g>
              <path
                class="st0"
                d="M256.007,357.113c-16.784,0-30.411,13.613-30.411,30.397c0,16.791,13.627,30.405,30.411,30.405
                s30.397-13.614,30.397-30.405C286.405,370.726,272.792,357.113,256.007,357.113z"
                style="fill: rgb(239, 68, 68)"></path>
              <path
                class="st0"
                d="M505.097,407.119L300.769,53.209c-9.203-15.944-26.356-25.847-44.777-25.847
                c-18.407,0-35.544,9.904-44.747,25.847L6.902,407.104c-9.203,15.943-9.203,35.751,0,51.694c9.204,15.943,26.356,25.84,44.763,25.84
                h408.67c18.406,0,35.559-9.897,44.762-25.84C514.301,442.855,514.301,423.047,505.097,407.119z M464.465,432.405
                c-2.95,5.103-8.444,8.266-14.35,8.266H61.878c-5.892,0-11.394-3.163-14.329-8.281c-2.964-5.11-2.979-11.445-0.014-16.548
                l194.122-336.24c2.943-5.103,8.436-8.274,14.35-8.274c5.9,0,11.386,3.171,14.336,8.282l194.122,336.226
                C467.415,420.945,467.415,427.295,464.465,432.405z"
                style="fill: rgb(239, 68, 68)"></path>
              <path
                class="st0"
                d="M256.007,152.719c-16.784,0-30.411,13.613-30.411,30.405l11.68,137.487c0,10.346,8.378,18.724,18.731,18.724
                c10.338,0,18.731-8.378,18.731-18.724l11.666-137.487C286.405,166.331,272.792,152.719,256.007,152.719z"
                style="fill: rgb(239, 68, 68)"></path>
            </g>
          </svg>
          <span class="ml-2 text-red-500 font-bold">模試受験における注意事項</span>
        </div>
        <ul class="mt-4 text-red-500 text-sm font-bold pb-2">
          <li>・試験は一度しか受験できません</li>
          <li class="my-2">・制限時間が経過すると強制的に試験終了となります</li>
          <li>・十分な時間を確保し実施してください</li>
        </ul>
      </div>
    </div>

    <div class="md:flex justify-center mx-auto">
      <div class="m-5 flex-none">
        <div class="col-span-4 shadow-lg bg-white mr-1 border border-gray-200 rounded-lg">
          <div class="p-4">
            <div class="flex justify-between mb-2">
              <div class="uppercase tracking-wide text-c2 mb-4">模試一覧</div>
              <div class="px-3 py-2 max-w-auto inline-flex font-semibold rounded-full bg-green-100 text-green-800">
                チケット残り枚数 x
                {{ props.userData.remaining_purchase_ticket }}
              </div>
            </div>
            <template v-if="isExistedWithinPeriodExam">
              <div
                v-for="(examination, i) in examinations"
                :key="i"
                :class="{
                  'bg-indigo-500': selectedExaminationIndex === i,
                  'text-white': selectedExaminationIndex === i,
                }">
                <template
                  v-if="examination.is_published && isWithinPeriodExam(examination)
                  ">
                  <button
                    class="w-full"
                    @click="selectExamination(i)">
                    <div
                      class="flex cursor-pointer border py-2 text-lg text-left text-grey-darkest hover:bg-indigo-500 hover:text-white">
                      <div class="pl-2 pt-1 text-sm">
                        {{ examination.title }}
                      </div>
                    </div>
                  </button>
                </template>
              </div>
            </template>
            <template v-else>
              <div
                class="w-full bg-red-100 border border-red-400 text-red-500 mt-2 px-12 py-3 rounded relative text-center"
                role="alert">
                <span class="block sm:inline font-bold">現在購入期間中の模試はありません。</span>
              </div>
            </template>
            <div class="my-5 border-b border-gray-200"></div>
            <div class="mt-4 border-2 rounded-md">
              <button
                class="flex items-center justify-between w-full p-3 text-left select-none"
                @click="toggleAccordion()">
                <span>過去の模試一覧</span>
                <svg
                  class="w-3 transition-all duration-200 transform"
                  :class="{
                    'rotate-180': isOpen,
                    'rotate-0': !isOpen,
                  }"
                  fill="none"
                  stroke="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 10">
                  <path
                    d="M15 1.2l-7 7-7-7"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </button>
              <div v-show="isOpen">
                <div
                  v-for="(examination, i) in examinations"
                  :key="i"
                  :class="{
                    'bg-indigo-500': selectedExaminationIndex === i,
                    'text-white': selectedExaminationIndex === i,
                  }">
                  <template
                    v-if="examination.is_published && isOutOfTermExam(examination)
                    ">
                    <button
                      class="w-full"
                      @click="selectExamination(i)">
                      <div
                        class="flex cursor-pointer border py-2 text-lg text-left text-grey-darkest hover:bg-indigo-500 hover:text-white">
                        <div class="pl-2 pt-1 text-sm">
                          {{ examination.title }}
                        </div>
                      </div>
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-span-8 flex-1 max-w-2xl m-5 text-gray-600 body-font">
        <div
          v-show="isShowSetPurchase(examinations[selectedExaminationIndex].id)"
          class="mb-5 text-gray-600">
          <div class="border border-gray-200 bg-white p-6 rounded-lg">
            <div class="w-10 h-10 inline-flex items-center justify-center mb-4">
              <svg
                id="_x32_"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                x="0px"
                y="0px"
                viewBox="0 0 512 512"
                style="width: 40px; height: 40px; opacity: 1"
                xml:space="preserve">
                <g>
                  <path
                    class="st0"
                    d="M193.845,405.991c-6.248,0-11.324,5.062-11.324,11.318c0,6.255,5.077,11.325,11.324,11.325
                    c6.256,0,11.325-5.07,11.325-11.325C205.17,411.053,200.101,405.991,193.845,405.991z"
                    style="fill: rgb(75, 75, 75)"></path>
                  <path
                    class="st0"
                    d="M193.845,363.678c-6.248,0-11.324,5.069-11.324,11.325c0,6.248,5.077,11.318,11.324,11.318
                    c6.256,0,11.325-5.07,11.325-11.318C205.17,368.746,200.101,363.678,193.845,363.678z"
                    style="fill: rgb(75, 75, 75)"></path>
                  <path
                    class="st0"
                    d="M491.425,227.51H205.17h-2.075h-20.575h-79.706H82.241v20.567v211.537v20.575h20.574h79.706h20.575h2.075
                    h286.254H512v-20.575V248.077V227.51H491.425z M491.425,459.614H205.17c0-6.255-5.069-11.318-11.325-11.318
                    c-6.248,0-11.324,5.062-11.324,11.318h-79.706V248.077h79.706c0,6.255,5.077,11.318,11.324,11.318
                    c6.256,0,11.325-5.062,11.325-11.318h286.254V459.614z"
                    style="fill: rgb(75, 75, 75)"></path>
                  <path
                    class="st0"
                    d="M193.845,321.372c-6.248,0-11.324,5.069-11.324,11.324c0,6.248,5.077,11.318,11.324,11.318
                    c6.256,0,11.325-5.07,11.325-11.318C205.17,326.441,200.101,321.372,193.845,321.372z"
                    style="fill: rgb(75, 75, 75)"></path>
                  <path
                    class="st0"
                    d="M193.845,279.065c-6.248,0-11.324,5.062-11.324,11.318c0,6.256,5.077,11.325,11.324,11.325
                    c6.256,0,11.325-5.069,11.325-11.325C205.17,284.127,200.101,279.065,193.845,279.065z"
                    style="fill: rgb(75, 75, 75)"></path>
                  <polygon
                    class="st0"
                    points="117.846,196.225 369.458,59.702 449.357,206.934 472.755,206.934 387.535,49.881 377.73,31.811
                    359.638,41.61 108.048,178.148 106.224,179.133 88.14,188.939 18.084,226.961 0,236.774 9.813,254.859 61.673,350.425
                    61.673,307.289 27.898,245.046 61.673,226.709 	"
                    style="fill: rgb(75, 75, 75)"></polygon>
                </g>
              </svg>
            </div>
            <h2 class="text-lg font-bold title-font">
              愛玩動物看護師国家試験対策 全国統一模試まとめ買い
            </h2>
            <p class="leading-relaxed text-base mb-2">(まとめ割:10,000円)</p>
            <p class="leading-relaxed text-base">
              以下の模試をまとめて購入します。
            </p>
            <p class="leading-relaxed text-base font-bold">
              ・愛玩動物看護師国家試験対策 全国統一模試【2025年度 夏】
            </p>
            <p class="leading-relaxed text-base font-bold">
              ・愛玩動物看護師国家試験対策 全国統一模試【2025年度 秋】
            </p>
            <p class="leading-relaxed text-base font-bold mb-2">
              ・愛玩動物看護師国家試験対策 全国統一模試【2025年度 国家試験直前】
            </p>
            <p class="leading-relaxed text-base">
              (購入する模試をお選びいただくことはできません)
            </p>
            <p class="leading-relaxed text-base">
              単体で模試を購入するよりお得です。
            </p>

            <button
              v-if="isOutOfTerm(15)"
              class="mt-2 px-8 py-2 rounded bg-gray-600 text-blue-50 max-w-max shadow-sm hover:opacity-50 opacity-50 cursor-not-allowed">
              購入期間外
            </button>
            <button
              v-else
              class="mt-2 px-8 py-2 rounded bg-blue-600 text-blue-50 max-w-max shadow-sm hover:opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="purchaseTicket()">
              購入する
            </button>
            <p
              v-if="isOutOfTermForTicket()"
              class="text-red-400 leading-relaxed text-base">
              ※{{ formatDate(examinations[selectedExaminationIndex].reception_start) }}よりご購入いただけます。
            </p>
          </div>
        </div>

        <div class="border border-gray-200 bg-white p-4 rounded-lg">
          <div class="w-10 h-10 inline-flex items-center justify-center mb-4">
            <svg
              id="_x32_"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              x="0px"
              y="0px"
              viewBox="0 0 512 512"
              style="width: 40px; height: 40px; opacity: 1"
              xml:space="preserve">
              <g>
                <polygon
                  class="st0"
                  points="93.539,218.584 275.004,218.584 354.699,138.894 355.448,138.145 355.448,125.045 93.539,125.045 	"
                  style="fill: rgb(75, 75, 75)"></polygon>
                <polygon
                  class="st0"
                  points="402.213,433.724 46.77,433.724 46.77,78.276 402.213,78.276 402.213,91.467 448.983,56.572
                  448.983,31.506 0,31.506 0,480.494 448.983,480.494 448.983,289.204 402.213,335.974 	"
                  style="fill: rgb(75, 75, 75)"></polygon>
                <path
                  class="st0"
                  d="M229.358,274.708H93.539v28.062h120.476C218.602,292.858,223.932,283.312,229.358,274.708z"
                  style="fill: rgb(75, 75, 75)"></path>
                <path
                  class="st0"
                  d="M93.539,349.539v28.062h110.935c-3.275-8.796-4.302-18.334-3.649-28.062H93.539z"
                  style="fill: rgb(75, 75, 75)"></path>
                <path
                  class="st0"
                  d="M290.939,268.789c-15.501,15.501-55.612,80.76-40.11,96.27c15.51,15.51,80.76-24.609,96.27-40.11l63.755-63.77
                  l-56.155-56.15L290.939,268.789z"
                  style="fill: rgb(75, 75, 75)"></path>
                <path
                  class="st0"
                  d="M500.374,115.509c-15.511-15.502-40.649-15.502-56.15,0l-76.682,76.685l56.156,56.15l76.676-76.685
                  C515.875,156.158,515.875,131.019,500.374,115.509z M400.166,202.361l-9.636-9.628l53.684-53.684l9.619,9.618L400.166,202.361z"
                  style="fill: rgb(75, 75, 75)"></path>
              </g>
            </svg>
          </div>
          <h2 class="text-lg font-bold title-font">
            {{ examinations[selectedExaminationIndex].title }}
          </h2>
          <p
            v-if="!examinations[selectedExaminationIndex].discount_price_id"
            class="leading-relaxed text-base mb-2">
            (通常:4,000円)
          </p>
          <p
            v-else-if="!examinations[selectedExaminationIndex].is_subscription_item
            "
            class="leading-relaxed text-base mb-2">
            (通常:4,000円、学割:3,200円)
          </p>
          <p
            v-else
            class="leading-relaxed text-base mb-2">
            (どうぶつ看護ポータル サブスクリプション契約者限定)
          </p>
          <div class="overflow-hidden sm:rounded-none py-4">
            <h2>購入期間</h2>
            <p class="ml-2">
              <span class="inline-block">{{
                formatDate(
                  examinations[selectedExaminationIndex].reception_start
                )
              }}
                〜</span>
              <span class="inline-block">{{
                formatDate(examinations[selectedExaminationIndex].reception_end)
              }}</span>
            </p>
          </div>
          <div
            v-if="isBeforeFY2024(examinations[selectedExaminationIndex].reception_start)"
            class="items-center justify-between overflow-hidden sm:rounded-none pb-4">
            <h2>実施期間(集計対象)</h2>
            <p class="ml-2">
              <span class="inline-block">{{
                formatDate(examinations[selectedExaminationIndex].start_date)
              }}
                〜</span>
              <span class="inline-block">{{
                formatDate(examinations[selectedExaminationIndex].end_date)
              }}</span>
            </p>
          </div>
          <div
            v-if="examinations[selectedExaminationIndex].result_announcement_date && isBeforeFY2024(examinations[selectedExaminationIndex].reception_start)"
            class="items-center justify-between overflow-hidden sm:rounded-none">
            <h2>実施期間(集計対象外)</h2>
            <p class="ml-2">
              <span class="inline-block">{{
                formatDate(
                  examinations[selectedExaminationIndex]
                    .result_announcement_date
                )
              }}
                〜</span>
              <span class="inline-block">{{
                formatDate(examinations[selectedExaminationIndex].reception_end)
              }}</span>
            </p>
          </div>
          <div
            v-if="!isBeforeFY2024(examinations[selectedExaminationIndex].reception_start)"
            class="items-center justify-between overflow-hidden sm:rounded-none pb-4">
            <h2>実施期間</h2>
            <p class="ml-2">
              <span class="inline-block">{{
                formatDate(examinations[selectedExaminationIndex].start_date)
              }}
                〜</span>
              <span class="inline-block">{{
                formatDate(examinations[selectedExaminationIndex].reception_end)
              }}</span>
            </p>
          </div>
          <div
            v-if="!isBeforeFY2024(examinations[selectedExaminationIndex].reception_start)"
            class="items-center justify-between overflow-hidden sm:rounded-none">
            <h2>成績集計開始日時</h2>
            <p class="ml-2">
              <span class="inline-block">{{
                formatDate(examinations[selectedExaminationIndex].result_announcement_date)
              }}
                〜</span>
            </p>
          </div>
          <div class="flex items-center justify-end overflow-hidden sm:rounded-none p-2">
            <button
              v-if="isPurchased(examinations[selectedExaminationIndex].id)"
              class="mt-2 px-8 py-2 rounded bg-red-500 text-blue-50 max-w-max shadow-sm hover:opacity-50 opacity-50 cursor-not-allowed">
              購入済み
            </button>
            <button
              v-else-if="isOutOfTerm(examinations[selectedExaminationIndex].id)"
              class="mt-2 px-8 py-2 rounded bg-gray-600 text-blue-50 max-w-max shadow-sm hover:opacity-50 opacity-50 cursor-not-allowed">
              購入期間外
            </button>
            <button
              v-else-if="examinations[selectedExaminationIndex].is_subscription_item
              "
              class="mt-2 px-8 py-2 rounded bg-blue-600 text-blue-50 max-w-max shadow-sm hover:opacity-75"
              @click="purchaseExam(examinations[selectedExaminationIndex])">
              無料で購入する
            </button>
            <button
              v-else
              class="mt-2 px-8 py-2 rounded bg-blue-600 text-blue-50 max-w-max shadow-sm hover:opacity-75"
              @click="purchaseExam(examinations[selectedExaminationIndex])">
              購入する
            </button>
          </div>
          <div
            v-for="(typeName, type) in examinationTypes"
            :key="type">
            <div class="flex sp:text-sm items-center justify-between overflow-hidden sm:rounded-none p-2">
              <p
                v-if="examinations[selectedExaminationIndex].is_subscription_item
                ">
                {{ typeName }}
              </p>
              <p v-else>{{ typeName }}{{ examinationTypesSpec[type] }}</p>
              <div v-if="isPurchased(examinations[selectedExaminationIndex].id)">
                <div
                  v-if="isFinished(
                    examinations[selectedExaminationIndex].id,
                    Number(type)
                  ) ||
                    isExpired(
                      examinations[selectedExaminationIndex],
                      Number(type)
                    )
                  "
                  class="bg-red-500 text-white py-2.5 px-10 sp:px-7 rounded-lg opacity-50 cursor-not-allowed">
                  実施済み
                </div>
                <button
                  v-else-if="isAvailableRetry(examinations[selectedExaminationIndex]) && isBeforeFY2024(examinations[selectedExaminationIndex].reception_start)
                  "
                  class="bg-blue-600 text-white py-2.5 px-10 sp:px-7 rounded-lg"
                  @click="
                    confirm(
                      examinations[selectedExaminationIndex],
                      Number(type)
                    )
                  ">
                  <div>開始する</div>
                  <div class="text-sm">(集計対象外)</div>
                </button>
                <button
                  v-else-if="isInProgress(
                    examinations[selectedExaminationIndex].id,
                    Number(type)
                  ) &&
                    !isExpired(
                      examinations[selectedExaminationIndex],
                      Number(type)
                    )
                  "
                  class="bg-green-600 lg:hover:bg-green-400 active:bg-green-400 duration-300 text-white py-2.5 px-10 sp:px-7 rounded-lg"
                  type="button"
                  @click="
                    resume(examinations[selectedExaminationIndex], Number(type))
                  ">
                  再開する
                </button>
                <button
                  v-else-if="isBeforeDate(
                    examinations[selectedExaminationIndex].start_date
                  )
                  "
                  class="bg-gray-500 text-white py-2.5 px-10 sp:px-7 rounded-lg opacity-50 cursor-not-allowed">
                  試験開始前
                </button>
                <button
                  v-else-if="isPrivilege() &&
                    !isInProgress(
                      examinations[selectedExaminationIndex].id,
                      Number(type)
                    ) &&
                    !isFinished(
                      examinations[selectedExaminationIndex].id,
                      Number(type)
                    )
                  "
                  class="bg-blue-600 lg:hover:bg-blue-400 active:bg-blue-400 duration-300 text-white py-2.5 px-10 sp:px-7 rounded-lg"
                  type="button"
                  @click="
                    confirm(
                      examinations[selectedExaminationIndex],
                      Number(type)
                    )
                  ">
                  開始する
                </button>
                <button
                  v-else-if="isOveredDate(
                    examinations[selectedExaminationIndex].reception_end
                  )
                  "
                  class="bg-red-500 text-white py-2.5 px-10 sp:px-7 rounded-lg opacity-50 cursor-not-allowed">
                  実施期間終了済
                </button>
                <button
                  v-else-if="!isInProgress(
                    examinations[selectedExaminationIndex].id,
                    Number(type)
                  ) &&
                    !isFinished(
                      examinations[selectedExaminationIndex].id,
                      Number(type)
                    )
                  "
                  class="bg-blue-600 lg:hover:bg-blue-400 active:bg-blue-400 duration-300 text-white py-2.5 px-10 sp:px-7 rounded-lg"
                  type="button"
                  @click="
                    confirm(
                      examinations[selectedExaminationIndex],
                      Number(type)
                    )
                  ">
                  開始する
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </app-client-layout>
</template>

<script setup>
import moment from 'moment'
import Swal from 'sweetalert2'
import { Inertia } from '@inertiajs/inertia'
import {
  computed, reactive, ref, watchEffect, watch, onMounted,
} from 'vue'
import { Link } from '@inertiajs/inertia-vue3'
import formatDate from '@/actions/format/date'
import AppClientLayout from '@/Layouts/AppClientLayout'

const props = defineProps({
  examinations: {
    type: Array,
    default: () => [],
  },
  examination: {
    type: Object,
    default: () => { },
  },
  examinationTypes: {
    type: Object,
    default: () => { },
  },
  examinationTypesSpec: {
    type: Object,
    default: () => { },
  },
  purchases: {
    type: Array,
    default: () => [],
  },
  progresses: {
    type: Array,
    default: () => [],
  },
  startAnswerTime: {
    type: String,
    default: '',
  },
  limitTimes: {
    type: Object,
    default: () => { },
  },
  userData: {
    type: Object,
    default: () => ({}),
  },
  flash: {
    type: Object,
    default: () => ({}),
  },
})

onMounted(() => {
  for (const [index, examination] of props.examinations.entries()) {
    if (isWithinPeriodExam(examination) && examination.is_published) {
      selectedExaminationIndex.value = index
      break
    }
  }
})

const state = reactive({
  selectedExaminations: [],
})

const onTransitionCheckContent = (num, id) => {
  Inertia.get(
    route('client-examinations.show', {
      client_examination: 'show',
      type: num,
      examination: id,
    }),
  )
}

const exam = ref()
const examType = ref()

const show = reactive({})
const onOpenCategory = (i) => {
  show[i] = !show[i]
}

const selectedExaminationIndex = ref(0)

const selectExamination = (index) => {
  selectedExaminationIndex.value = index
}

const isOpen = ref(false)

const toggleAccordion = () => {
  isOpen.value = !isOpen.value
}

const flashObject = computed(() => ({
  flash: props.flash,
}))

const isExistedWithinPeriodExam = computed(() => {
  for (const examination of props.examinations) {
    if (isWithinPeriodExam(examination) && examination.is_published) {
      return true
    }
  }
  return false
})

const isOutOfTermForTicket = () => {
  const examObj = props.examinations.find(
    (examination) => examination.id === 15,
  )
  const now = moment()
  const receptionStart = moment(examObj.reception_start)
  if (now.isBefore(receptionStart)) {
    return true
  }
  return false
}

const isOutOfTerm = (id) => {
  const examObj = props.examinations.find(
    (examination) => examination.id === id,
  )
  const now = moment()
  const receptionStart = moment(examObj.reception_start)
  const receptionEnd = moment(examObj.reception_end)
  if (now.isBefore(receptionStart) || now.isAfter(receptionEnd)) {
    return true
  }
  return false
}

const isShowWarning = (id) => {
  const examObj = props.examinations.find(
    (examination) => examination.id === id,
  )
  const now = moment()
  const receptionEnd = moment(examObj.reception_end)
  if (now.diff(receptionEnd, 'days') > -1) {
    return true
  }
  return false
}

const isPrivilege = () => props.userData.is_privilege

const isShowSetPurchase = (id) => {
  // TODO:examinationsテーブルにカラムを追加する
  if (id === 15 || id === 16 || id === 17) {
    if (isPurchased(15) || isPurchased(16) || isPurchased(17)) {
      return false
    }
    return true
  }
  return false
}

const purchaseTicket = () => {
  // ユーザ情報が入力されていなければユーザ情報入力用のモーダルを表示する
  if (isBlankUserInfo()) {
    showRequiredUserInputModal()
  } else {
    Inertia.post(route('purchase-ticket'))
  }
}

const purchaseExam = (examination) => {
  exam.value = examination

  if (isOutOfTerm(examination.id)) {
    Swal.fire({
      title: '購入期間外です。',
      icon: 'warning',
      confirmButtonText: '閉じる',
    })
    return
  }
  if (isShowWarning(examination.id)) {
    Swal.fire({
      icon: 'warning',
      title: '実施終了日時が近づいています。',
      html: '実施終了日時までに<br>試験開始できなかった模試種別は<br>受験できませんのでご注意ください。<br>購入画面に進みますか？',
      showCancelButton: true,
      confirmButtonText: 'はい',
      cancelButtonText: 'いいえ',
    }).then((result) => {
      if (!result.isConfirmed) {

      }
    })
  }

  if (isBlankUserInfo()) {
    showRequiredUserInputModal()
  } else if (examination.is_subscription_item) {
    Inertia.post(
      route('store', {
        examination: examination.id,
      }),
    )
  } else if (props.userData.remaining_purchase_ticket == 0) {
    if (isOutOfTerm(15)) {
      // 夏模試の購入期間が終わったらまとめ売りチケット販売を終了する。
      showPurchaseTypeModal(false)
    } else {
      // チケットがない場合
      Swal.fire({
        icon: 'warning',
        title: 'チケットをまとめて購入した方がお安くお買い求めいただけます。',
        text: '単体で購入してもよろしいですか？',
        showCancelButton: true,
        confirmButtonText: 'はい',
        cancelButtonText: 'いいえ',
      }).then((result) => {
        if (result.isConfirmed) {
          showPurchaseTypeModal(false)
        }
      })
    }
  } else {
    Swal.fire({
      icon: 'question',
      title: 'チケットを消費して模試を購入します。',
      text: 'この操作は取り消せません。よろしいですか？',
      showCancelButton: true,
      confirmButtonText: 'はい',
      cancelButtonText: 'いいえ',
    }).then((result) => {
      if (result.isConfirmed) {
        Inertia.post(
          route('purchase-use-ticket', {
            exam_id: exam.value.id,
          }),
        )
      }
    })
  }
}

const showRequiredUserInputModal = () => {
  Swal.fire({
    icon: 'warning',
    title: 'ユーザ情報を入力してください',
    showCancelButton: true,
    confirmButtonText: '進む',
    cancelButtonText: '戻る',
  }).then((result) => {
    if (result.isConfirmed) {
      Inertia.get(route('client.profile.show'))
    }
  })
}

const showRequiredSchoolInputModal = () => {
  Swal.fire({
    icon: 'warning',
    title: '学生情報を入力してください',
    text: 'プロフィール変更画面の学割情報にチェックを入れて情報を入力してください',
    showCancelButton: true,
    confirmButtonText: '進む',
    cancelButtonText: '戻る',
  }).then((result) => {
    if (result.isConfirmed) {
      Inertia.get(route('client.profile.show'))
    }
  })
}

const showPurchaseTypeModal = (isTicket) => {
  // discount_price_idがある場合、通常価格か、学割かを選択するダイアログを表示する。
  if (exam.value.discount_price_id) {
    Swal.fire({
      icon: 'question',
      title: '購入種別をお選びください',
      confirmButtonText: '通常価格で購入する',
      showCancelButton: true,
      cancelButtonText: 'キャンセルする',
      showDenyButton: true,
      denyButtonText: '学割価格で購入する',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isTicket) {
          Inertia.post(route('purchase-ticket'))
        } else {
          Inertia.get(
            route('purchase', {
              examination: exam.value.id,
            }),
          )
        }
      } else if (result.isDenied) {
        if (!props.userData.is_input_school_info) {
          showRequiredSchoolInputModal()
        } else if (
          props.userData.is_input_school_info
          && (!props.userData.school
            || !props.userData.department
            || !props.userData.student_id
            || !props.userData.date_of_admission)
        ) {
          showRequiredSchoolInputModal()
        } else if (isTicket) {
          Inertia.post(route('purchase-ticket'), {
            is_student: true,
          })
        } else {
          Inertia.get(
            route('purchase', {
              examination: exam.value.id,
              is_student: true,
            }),
          )
        }
      }
    })
  } else if (isTicket) {
    Inertia.post(route('purchase-ticket'))
  } else {
    Inertia.get(
      route('purchase', {
        examination: exam.value.id,
      }),
    )
  }
}

const isBlankUserInfo = () => {
  let isBlank = false

  // 学生情報が入力される場合は基本情報を必須にしない
  if (!props.userData.is_input_school_info) {
    if (
      !props.userData.name
      || props.userData.sex == null
      || !props.userData.postal_code
      || String(props.userData.postal_code).length < 7
      || !props.userData.address
      || !props.userData.job
      || !props.userData.service_years
    ) {
      isBlank = true
    } else if (props.userData.job === 13 && !props.userData.job_text) {
      isBlank = true
    } else if (
      props.userData.service_years === 10
      && !props.userData.service_years_text
    ) {
      isBlank = true
    }
  }

  // 学生情報が有効な場合は学生情報の必須チェック
  if (
    props.userData.is_input_school_info
    && (!props.userData.name
      || !props.userData.service_years
      || !props.userData.school
      || !props.userData.department
      || !props.userData.school_email
      || !props.userData.student_id)
  ) {
    isBlank = true
  }

  return isBlank
}

const resume = (examination, type) => {
  const json = localStorage.getItem('answers')
  const array = json != null ? JSON.parse(json) : []

  const form = Inertia.form({
    answerList: array,
    examination,
    examination_type: type,
  })
  form.post('/play')

  show.value = false
}

const isExpired = (examination, type) => {
  const now = moment().format('YYYY-MM-DD HH:mm:ss')

  let limitSeconds = 0
  if (examination.is_subscription_item) {
    limitSeconds = props.limitTimes[type] / 2
  } else {
    limitSeconds = props.limitTimes[type]
  }

  // 試験の終了時間
  const finishExaminationTime = computed(() => {
    const conversionMinutes = limitSeconds / 60
    const progressObj = props.progresses.find(
      (progress) => progress.examination_id === examination.id
        && progress.examination_type === type,
    )
    if (progressObj == null) {
      return 0
    }
    return moment(progressObj.started_at).add(conversionMinutes, 'm').format()
  })

  if (finishExaminationTime.value === 0) {
    return false
  }

  const diffSeconds = moment(finishExaminationTime.value).diff(moment(now))
  if (diffSeconds < 0) {
    const json = localStorage.getItem('answers')
    const answerList = json != null ? JSON.parse(json) : []
    const form = reactive(
      Inertia.form({
        answerList,
        examination,
        type,
      }),
    )
    form.post(route('client-examinations.finish'))
    return true
  }
  return false
}

const isBeforeDate = (data) => {
  const now = moment()
  const momentDate = moment(data)
  return now.isBefore(momentDate)
}

const isOveredDate = (data) => {
  const now = moment()
  const momentDate = moment(data)
  return now.isAfter(momentDate)
}

const isPurchased = (id) => props.purchases.filter((purchase) => purchase.exam_id === id).length

const isInProgress = (id, type) => props.progresses.filter(
  (progress) => progress.examination_id === id
      && progress.examination_type === type
      && progress.started_at !== null
      && progress.finished_at === null,
).length

const isFinished = (id, type) => props.progresses.filter(
  (progress) => progress.examination_id === id
      && progress.examination_type === type
      && progress.finished_at !== null,
).length

const isAllFinished = (id) => {
  const progresses = props.progresses.filter(
    (progress) => progress.examination_id === id,
  )

  if (progresses.length < 3) {
    return false
  }
  for (const element of progresses) {
    if (!element.finished_at) {
      return false
    }
  }
  return true
}

const isAvailableRetry = (examination) => {
  if (
    isOveredDate(examination.reception_end)
    || !isOveredDate(examination.start_date)
  ) {
    return false
  }

  if (isOveredDate(examination.result_announcement_date)) {
    return true
  }

  if (
    examination.result_announcement_date == null
    && isAllFinished(examination.id)
  ) {
    return true
  }
  return false
}

const play = (examination, type) => {
  localStorage.clear()
  const form = Inertia.form({
    examination,
    examination_type: type,
  })
  form.post('/play')
}

const confirm = (examination, type) => {
  let isProgress = false
  switch (type) {
    case 1:
      isProgress = isInProgress(examination.id, 2) || isInProgress(examination.id, 3)
      break
    case 2:
      isProgress = isInProgress(examination.id, 1) || isInProgress(examination.id, 3)
      break
    case 3:
      isProgress = isInProgress(examination.id, 1) || isInProgress(examination.id, 2)
      break
    default:
      isProgress = false
      break
  }

  if (isProgress) {
    Swal.fire({
      title: '他の試験を実施中です。',
      text: '先に実施中の試験を終了させてください。',
      icon: 'warning',
      confirmButtonText: '閉じる',
    })
    return
  }
  exam.value = examination
  examType.value = type

  Swal.fire({
    icon: 'question',
    text: '試験を開始してもよろしいですか？',
    showCancelButton: true,
    confirmButtonText: 'はい',
    cancelButtonText: 'いいえ',
  }).then((result) => {
    if (result.isConfirmed) {
      play(exam, examType)
    }
  })
}

window.history.pushState(null, null, null)
window.addEventListener('popstate', () => {
  window.history.pushState(null, null, null)
})

const isWithinPeriodExam = (examination) => {
  const now = moment()
  return now.isBefore(examination.reception_end)
}

const isOutOfTermExam = (examination) => {
  const now = moment()
  return now.isAfter(examination.reception_end)
}

const isBeforeFY2024 = (date) => {
  // 2025年度以前であればtrueを返す
  const FY2024 = moment('2025-04-01 00:00:00')
  const momentDate = moment(date)
  return momentDate.isBefore(FY2024)
}
</script>
